import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { useMetricsData, DailyRate, DailyStats, Stats } from '../../hooks/useMetricsData';

/** Parse YYYY-MM-DD as local midnight (not UTC) */
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Format YYYY-MM-DD as "Mar 22" */
function formatDateLabel(dateStr: string): string {
  return parseLocalDate(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Generate all date keys (day or week) in the range */
function allDateKeys(days: number): string[] {
  const useWeeks = days >= 90;
  const keys: string[] = [];
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days + 1);

  if (useWeeks) {
    // Align start to Monday
    const day = start.getDay();
    start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
  }

  const cursor = new Date(start);
  const step = useWeeks ? 7 : 1;
  while (cursor <= now) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
    keys.push(key);
    cursor.setDate(cursor.getDate() + step);
  }
  return keys;
}

// --- Stat line config ---

type StatLineKey = 'min' | 'median' | 'p90' | 'max';

function useStatLines() {
  const { palette } = useTheme();
  return [
    { key: 'min' as StatLineKey, label: 'Min', color: palette.metrics.min },
    { key: 'median' as StatLineKey, label: 'Median', color: palette.metrics.median },
    { key: 'p90' as StatLineKey, label: 'P90', color: palette.metrics.p90 },
    { key: 'max' as StatLineKey, label: 'Max', color: palette.metrics.max },
  ];
}

// --- Chart helper components ---

function FailureChart({ title, data, dateKeys }: { title: string; data: DailyRate[]; dateKeys: string[] }) {
  const { palette } = useTheme();
  if (data.length === 0) return <EmptyChart title={title} />;
  const byDate: Record<string, DailyRate> = {};
  data.forEach((d) => { byDate[d.date] = d; });
  const filled = dateKeys.map((date) => byDate[date] || { date, total: 0, failed: 0, rate: 0 });
  return (
    <ChartCard title={title}>
      <BarChart
        xAxis={[{ data: filled.map((d) => formatDateLabel(d.date)), scaleType: 'band' }]}
        series={[
          {
            data: filled.map((d) => d.total - d.failed),
            label: 'Succeeded',
            color: palette.metrics.succeeded,
            valueFormatter: (v, { dataIndex }) => {
              const d = filled[dataIndex];
              return d ? `${d.total - d.failed} of ${d.total} (${(100.0 - d.rate).toFixed(0)}%)` : String(v);
            },
            stack: 'total',
          },
          {
            data: filled.map((d) => d.failed),
            label: 'Failed',
            color: palette.metrics.failed,
            stack: 'total',
            valueFormatter: (v, { dataIndex }) => {
              const d = filled[dataIndex];
              return d ? `${d.failed} of ${d.total} (${d.rate.toFixed(0)}%)` : String(v);
            },
          },
        ]}
        height={300}
      />
    </ChartCard>
  );
}

function DurationChart({ title, data, dateKeys, visibleLines }: { title: string; data: DailyStats[]; dateKeys: string[]; visibleLines: Set<StatLineKey> }) {
  const statLines = useStatLines();
  if (data.length === 0) return <EmptyChart title={title} />;
  const byDate: Record<string, DailyStats> = {};
  data.forEach((d) => { byDate[d.date] = d; });
  const dates = dateKeys.map((k) => parseLocalDate(k));
  const series = [...statLines]
    .reverse()
    .filter((l) => visibleLines.has(l.key))
    .map((l) => ({
      data: dateKeys.map((k) => byDate[k] ? byDate[k][l.key] : null),
      label: l.label,
      color: l.color,
      connectNulls: true,
    }));
  return (
    <ChartCard title={title}>
      <LineChart
        xAxis={[{
          data: dates,
          scaleType: 'time',
          min: dates[0],
          max: dates[dates.length - 1],
          valueFormatter: (v: Date) => v.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        }]}
        yAxis={[{ label: 'Minutes' }]}
        series={series}
        height={300}
      />
    </ChartCard>
  );
}

function PRStatsCard({ label, stats, visibleLines, format }: {
  label: string;
  stats: Stats | null;
  visibleLines: Set<StatLineKey>;
  format: (v: number) => string;
}) {
  const statLines = useStatLines();
  if (!stats) return <StatCard label={label} value="--" />;
  const visible = statLines.filter((l) => visibleLines.has(l.key));
  return (
    <Card variant="outlined">
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          {visible.map((l) => (
            <Box key={l.key}>
              <Typography variant="h5" fontWeight={700} sx={{ color: l.color }}>
                {format(stats[l.key])}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {l.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyChart({ title }: { title: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, color }: { label: string; value: string, color?: string }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h4" fontWeight={700} color={color ?? 'text.primary'}>
          {value}
        </Typography>
        <Typography variant="body2" color={color ?? "text.secondary"}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function formatHours(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function formatCount(v: number): string {
  return v.toFixed(0);
}

// --- Main Page ---

const MetricsPage: React.FC = () => {
  const { palette } = useTheme();
  const statLines = useStatLines();
  const [days, setDays] = useState<number>(30);
  const [visibleLines, setVisibleLines] = useState<StatLineKey[]>(['min', 'median', 'p90', 'max']);
  const metrics = useMetricsData(days);

  const dateKeys = allDateKeys(days);
  const visibleSet = new Set(visibleLines);

  const toggleLine = (key: StatLineKey) => {
    setVisibleLines((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  if (metrics.loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading metrics...</Typography>
      </Box>
    );
  }

  if (metrics.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{metrics.error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Metrics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Stat line toggles */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {statLines.map((l) => (
              <Chip
                key={l.key}
                label={l.label}
                size="small"
                onClick={() => toggleLine(l.key)}
                variant={visibleSet.has(l.key) ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: visibleSet.has(l.key) ? l.color : 'transparent',
                  color: visibleSet.has(l.key) ? '#fff' : l.color,
                  borderColor: l.color,
                  '&:hover': { opacity: 0.85 },
                }}
              />
            ))}
          </Box>
          {/* Date range selector */}
          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={(_, v) => v !== null && setDays(v)}
            size="small"
          >
            <ToggleButton value={7}>7 days</ToggleButton>
            <ToggleButton value={30}>30 days</ToggleButton>
            <ToggleButton value={90}>90 days</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Row 1: Summary stat cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Total Rollouts" value={metrics.totalRollouts.toString()} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Rollout Failure Rate" value={`${metrics.overallRolloutFailureRate.toFixed(0)}%`} color={palette.metrics.failed} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Total Test Runs" value={metrics.totalTestRuns.toString()} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Test Failure Rate" value={`${metrics.overallTestFailureRate.toFixed(0)}%`} color={palette.metrics.failed} />
        </Grid>
      </Grid>

      {/* Row 2: PR lifecycle stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <PRStatsCard label="PR Creation to Merge" stats={metrics.creationToMergeStats} visibleLines={visibleSet} format={formatHours} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <PRStatsCard label="Deploy to Approval" stats={metrics.deployToApprovalStats} visibleLines={visibleSet} format={formatHours} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <PRStatsCard label="Approval to Merge" stats={metrics.approvalToMergeStats} visibleLines={visibleSet} format={formatHours} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <PRStatsCard label="Comments per PR" stats={metrics.commentsPerPRStats} visibleLines={visibleSet} format={formatCount} />
        </Grid>
      </Grid>

      {/* Charts in 2-column grid */}
      <Grid container spacing={2}>
        {/* Volume charts */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <FailureChart title="Rollout Volume" data={metrics.rolloutFailureRates} dateKeys={dateKeys} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <FailureChart title="Module Deployment Volume" data={metrics.deploymentFailureRates} dateKeys={dateKeys} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <FailureChart title="Setup Task Volume" data={metrics.setupTaskFailureRates} dateKeys={dateKeys} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <FailureChart title="Test Volume" data={metrics.testFailureRates} dateKeys={dateKeys} />
        </Grid>

        {/* Duration charts */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <DurationChart title="Rollout Duration" data={metrics.rolloutDurations} dateKeys={dateKeys} visibleLines={visibleSet} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DurationChart title="Module Deployment Duration" data={metrics.deploymentDurations} dateKeys={dateKeys} visibleLines={visibleSet} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DurationChart title="Setup Task Duration" data={metrics.setupTaskDurations} dateKeys={dateKeys} visibleLines={visibleSet} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DurationChart title="Test Run Duration (per subject)" data={metrics.testDurations} dateKeys={dateKeys} visibleLines={visibleSet} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MetricsPage;
